import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Trash2, X } from 'lucide-react';
import legacyUapiService from '../services/LegacyUapiService';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/datetime';
import Spinner from './shared/Spinner';
import UapiModuleCalls from './shared/UapiModuleCalls';
import CopyButton from './shared/CopyButton';
import ConfirmModal from './shared/ConfirmModal';
import CredentialSelector, { joinOverrides, splitOverrides } from './shared/CredentialSelector';
import UapiCustomCodeAttachments from './shared/UapiCustomCodeAttachments';
import './OhipTransactionsPage.css';
import './UniversalApiPage.css';
import './PaymentPorterPage.css';           // .pp-back-btn, .pp-rules-add-btn, .pp-criteria-add-btn
import './AddPaymentPorterWizard.css';      // .ppw-error-msg

// One backend-system binding: what the Universal API calls that system with for this property.
//
// WHAT IS EDITABLE, AND WHAT IS NOT
// The credential and the per-hotel values it needs are settings — a credential is rotated, a property
// code is corrected, and neither changes what the binding IS. The SYSTEM and the x-hotelid are not
// editable, because the identifier encodes the system it routes to and has already been handed to a
// caller: re-pointing it would send that caller's traffic somewhere else under an identifier that now
// lies about where it goes. Changing system is removing this binding and adding one, which issues a new
// identifier — visibly, rather than by quietly repurposing the old one.
function UapiAccessEditPage() {
    const navigate = useNavigate();
    const { hotelId, accessId } = useParams();
    const { selectedAccount, selectedPartner } = useAccount();
    const { isSuperAdmin, solePartnerId } = useAuth();

    const [access, setAccess] = useState(null);
    const [credential, setCredential] = useState(null);      // the selector's value
    const [hotelidOverride, setHotelidOverride] = useState('');
    const [overrides, setOverrides] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(false);
    // 'Y' → this binding takes its allowed modules from the SYSTEM (marketplace_modules) instead of the
    // credential's property-wide grants. Saved on click like every other toggle in the portal.
    const [overrideModules, setOverrideModules] = useState('N');
    const [togglingOverride, setTogglingOverride] = useState(false);
    // This binding's own module list, and what it may contain — the credential's grants. The override
    // narrows an entitlement; it can never allow a path the property was not granted.
    const [modules, setModules] = useState([]);
    const [grantable, setGrantable] = useState([]);
    const [pickedRoutes, setPickedRoutes] = useState([]);
    const [moduleBusy, setModuleBusy] = useState(false);

    const hotelUrl = `/universal-api/authentication/${hotelId}`;

    const errText = (e, fallback) =>
        e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || fallback;

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await legacyUapiService.getAccess(accessId);
            const a = res.data || res;
            setAccess(a);
            // The selector wants the shape the picker returns; the row carries the same facts under the
            // binding's own column names.
            setCredential(a.credential_id ? {
                credential_id: a.credential_id,
                credential_name: a.credential_name,
                entity_type: a.credential_hotel_id ? 'hotel'
                    : a.credential_account_id ? 'account'
                    : a.credential_partner_id ? 'partner' : undefined,
            } : null);
            setHotelidOverride(a.hotelid_override || '');
            setOverrideModules(a.override_modules === 'Y' ? 'Y' : 'N');
            setModules(a.modules || []);
            setGrantable(a.grantable || []);
            setPickedRoutes([]);
            setOverrides(typeof a.credential_overrides === 'string'
                ? JSON.parse(a.credential_overrides || '{}')
                : (a.credential_overrides || {}));
        } catch (e) {
            setError(errText(e, 'Could not load this backend system'));
        } finally {
            setLoading(false);
        }
    }, [accessId]);

    useEffect(() => { load(); }, [load]);

    const openCode = (codeId) => navigate(`/universal-api/custom-code/${codeId}?accessId=${accessId}`);

    // Only what changed is sent. The server writes exactly the keys it receives, so this is what keeps a
    // save from blanking a field the form never touched.
    const save = async () => {
        if (!credential?.credential_id) { setError('A credential is required'); return; }
        setSaving(true);
        setError(null);
        try {
            const patch = {};
            if (String(credential.credential_id) !== String(access.credential_id)) {
                patch.credentialId = credential.credential_id;
            }
            if ((hotelidOverride || '') !== (access.hotelid_override || '')) {
                patch.hotelidOverride = hotelidOverride || null;
            }
            const originalOverrides = typeof access.credential_overrides === 'string'
                ? JSON.parse(access.credential_overrides || '{}')
                : (access.credential_overrides || {});
            if (JSON.stringify(overrides || {}) !== JSON.stringify(originalOverrides)) {
                patch.credentialOverrides = overrides;
            }
            if (Object.keys(patch).length) await legacyUapiService.updateAccess(accessId, patch);
            navigate(hotelUrl);
        } catch (e) {
            setError(errText(e, 'Could not save this backend system'));
            setSaving(false);
        }
    };

    const toggleOverride = async () => {
        const next = overrideModules === 'Y' ? 'N' : 'Y';
        setTogglingOverride(true);
        setError(null);
        try {
            const res = await legacyUapiService.updateAccess(accessId, { overrideModules: next });
            const a = res.data || res;
            setOverrideModules(next);
            setAccess(prev => ({ ...prev, ...a }));
            // Switching on seeds the list from the credential's grants, so the response is the answer.
            setModules(a.modules || []);
            setGrantable(a.grantable || []);
            setPickedRoutes([]);
        } catch (e) {
            setError(errText(e, 'Could not change the module source'));
        } finally {
            setTogglingOverride(false);
        }
    };

    const togglePicked = (routeId) => {
        const id = String(routeId);
        setPickedRoutes(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
    };

    const allowModules = async () => {
        if (!pickedRoutes.length) return;
        setModuleBusy(true);
        setError(null);
        try {
            const res = await legacyUapiService.addAccessModules(accessId, pickedRoutes.map(Number));
            const d = res.data || res;
            setModules(d.modules || []);
            setPickedRoutes([]);
        } catch (e) {
            setError(errText(e, 'Could not add that module'));
        } finally {
            setModuleBusy(false);
        }
    };

    const disallowModule = async (m) => {
        setModuleBusy(true);
        setError(null);
        try {
            await legacyUapiService.removeAccessModule(accessId, m.id);
            setModules(prev => prev.filter(x => x.id !== m.id));
        } catch (e) {
            setError(errText(e, 'Could not remove that module'));
        } finally {
            setModuleBusy(false);
        }
    };

    const remove = async () => {
        setSaving(true);
        setError(null);
        try {
            await legacyUapiService.deleteAccess(accessId);
            navigate(hotelUrl);
        } catch (e) {
            setError(errText(e, 'Could not remove this backend system'));
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="otx-page">
                <div className="loading-section"><Spinner size="md" /><p>Loading backend system...</p></div>
            </div>
        );
    }

    return (
        <div className="otx-page">
            <button className="pp-back-btn" onClick={() => navigate(hotelUrl)}>
                <ArrowLeft className="icon-sm" /> Back to {access?.hotelname || 'the hotel'}
            </button>

            <div className="otx-header">
                <div>
                    {/* The binding's name titles the page when it has one — with several bindings to one
                        system, "Opera Cloud via OHIP" names the page you are on and the two you are not. */}
                    <h1>{access?.label || access?.system_name}</h1>
                    <p className="otx-subtitle">
                        How the Universal API reaches {access?.system_name} for {access?.hotelname}.
                    </p>
                </div>
            </div>

            {error && (
                <div className="error-alert">
                    <div className="error-alert-content">
                        <AlertCircle className="error-alert-icon" />
                        <p className="error-alert-message">{error}</p>
                    </div>
                </div>
            )}

            {/* The two facts that cannot change here, stated rather than rendered as disabled inputs —
                a greyed-out field invites the reader to look for the way to enable it. */}
            <div className="uapi-modules uapi-modules-standalone">
                <div className="uapi-issued-row">
                    <span className="uapi-issued-label">Backend system</span>
                    <span className="uapi-system-chip">{access?.system_name}</span>
                </div>
                <div className="uapi-issued-row">
                    <span className="uapi-issued-label">x-hotelid</span>
                    <code className="uapi-issued-value">{access?.uapi_hotel_id}</code>
                    <CopyButton value={access?.uapi_hotel_id} label="Copy" className="copy-btn-inline" />
                </div>
                <div className="uapi-issued-row">
                    <span className="uapi-issued-label">Added</span>
                    <span>{formatDateTime(access?.created_at)}</span>
                </div>
                <p className="lu-flow-note" style={{ marginTop: '0.75rem' }}>
                    The system and its routing identifier are fixed for the life of the binding — callers
                    hold that identifier, and it names the system it reaches. To route this property to a
                    different system, remove this binding and add one; that issues a new identifier.
                </p>
            </div>

            {/* CUSTOM CODE (V528) — what differs for THIS client on this system. The integration is the
                same for every property; a snippet attached here runs before the call (it can rewrite the
                request, or set values the integration reads — the Azure blob name, say) and after the
                answer. The list is the shared one the pipelines designer's Integration column also opens. */}
            <UapiCustomCodeAttachments
                target={{ kind: 'access', id: accessId }}
                systemName={access?.system_name}
                intro={<>
                    Runs on calls to {access?.system_name} for this property — only in the modules each one
                    targets — in this order:{' '}
                    <code>before()</code> ahead of the call, <code>after()</code> once it answers.
                </>}
                emptyText={`No custom code — ${access?.system_name || 'this system'} is called exactly as its `
                    + 'integration does for every property.'}
                detachTitle={(c) => `Stop running ${c.name} for this property — the code itself is kept`}
                onOpen={openCode}
                onNew={() => navigate(`/universal-api/custom-code/new?accessId=${accessId}`)}
                onError={setError}
            />

            {/* MODULES for this binding. The default is the credential's property-wide grants; turning
                this on takes the allowed set from what the SYSTEM declares it serves instead. That is
                the case a property-wide list cannot express — a CRM bound alongside a PMS serves
                /profile and not /rsv, and granting both on the credential would appear to allow /rsv
                through the CRM. There is no second list to maintain: the answer already exists on the
                system, and this says to use it. */}
            <div className="uapi-modules uapi-modules-standalone">
                <div className="uapi-section-bar">
                    <h2 className="uapi-section-title">Modules</h2>
                    {/* The toggle IS the control — saved on click, no separate Save. */}
                    <button
                        className={`pp-toggle-btn ${overrideModules === 'Y' ? 'pp-toggle-btn-on' : 'pp-toggle-btn-off'}`}
                        onClick={toggleOverride}
                        disabled={togglingOverride}
                        title={overrideModules === 'Y'
                            ? 'Click to use the credential\u2019s modules instead'
                            : 'Click to use this system\u2019s own modules instead'}
                    >
                        <span className="pp-toggle-track"><span className="pp-toggle-thumb" /></span>
                        <span className="pp-toggle-label">
                            {togglingOverride ? 'Saving…' : 'Override modules'}
                        </span>
                    </button>
                </div>

                {overrideModules === 'Y' ? (
                    <>
                        <p className="lu-flow-note" style={{ marginTop: 0 }}>
                            Requests routed to {access?.system_name} for this property may call the modules
                            below. Chosen from what the property's Universal API credential is granted — a
                            binding narrows that entitlement, it cannot widen it.
                        </p>

                        {(() => {
                            const remaining = grantable.filter(
                                g => !modules.some(m => String(m.uapi_route_id) === String(g.id)));
                            if (!grantable.length) {
                                return (
                                    <p className="ppw-error-msg" style={{ margin: 0 }}>
                                        The property's Universal API credential has no modules granted, so
                                        there is nothing this binding can allow. Grant them on the hotel's
                                        page first.
                                    </p>
                                );
                            }
                            if (!remaining.length) {
                                return (
                                    <p className="lu-muted" style={{ margin: 0 }}>
                                        Every module the credential holds is allowed through this system.
                                    </p>
                                );
                            }
                            const allPicked = pickedRoutes.length === remaining.length;
                            return (
                                <>
                                    <label className="uapi-module-check uapi-module-all">
                                        <input
                                            type="checkbox"
                                            checked={allPicked}
                                            ref={(el) => {
                                                if (el) el.indeterminate = pickedRoutes.length > 0 && !allPicked;
                                            }}
                                            onChange={() => setPickedRoutes(
                                                allPicked ? [] : remaining.map(r => String(r.id)))}
                                            disabled={moduleBusy}
                                        />
                                        Select all ({remaining.length})
                                    </label>
                                    <div className="uapi-module-grid">
                                        {remaining.map(r => (
                                            <label key={r.id} className="uapi-module-check">
                                                <input
                                                    type="checkbox"
                                                    checked={pickedRoutes.includes(String(r.id))}
                                                    onChange={() => togglePicked(r.id)}
                                                    disabled={moduleBusy}
                                                />
                                                {r.path}
                                            </label>
                                        ))}
                                    </div>
                                    <div className="uapi-module-add">
                                        <button
                                            type="button"
                                            className="pp-rules-add-btn"
                                            onClick={allowModules}
                                            disabled={!pickedRoutes.length || moduleBusy}
                                        >
                                            {moduleBusy ? 'Adding…'
                                                : `Allow ${pickedRoutes.length || ''} module${pickedRoutes.length === 1 ? '' : 's'}`}
                                        </button>
                                        {pickedRoutes.length > 0 && (
                                            <button
                                                type="button"
                                                className="pp-back-btn"
                                                style={{ margin: 0 }}
                                                onClick={() => setPickedRoutes([])}
                                                disabled={moduleBusy}
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                </>
                            );
                        })()}

                        {modules.length === 0 ? (
                            <p className="ppw-error-msg" style={{ margin: '0.75rem 0 0' }}>
                                Nothing is allowed through {access?.system_name} for this property while the
                                override is on and this list is empty.
                            </p>
                        ) : (
                            <div className="uapi-module-chips">
                                {modules.map(m => (
                                    <span key={m.id} className="uapi-module-chip">
                                        {m.path}
                                        {/* The same narrowing as the credential level, from the same
                                            component. The dispatcher checks THIS list when the override
                                            is on, so a narrowing that only existed on the credential
                                            would be bypassed by every binding that overrides. */}
                                        <UapiModuleCalls
                                            level="access"
                                            module={m}
                                            disabled={moduleBusy}
                                            onSaved={load}
                                        />
                                        <button
                                            type="button"
                                            className="uapi-module-chip-remove"
                                            title={`Stop allowing ${m.path} through ${access?.system_name}`}
                                            disabled={moduleBusy}
                                            onClick={() => disallowModule(m)}
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <p className="lu-flow-note" style={{ marginTop: 0 }}>
                        This binding allows whatever the property's Universal API credential is granted —
                        managed on the hotel's page, and shared by every system it is routed to. Turn the
                        override on to choose a narrower set for {access?.system_name}; it starts as a copy
                        of the credential's grants, so nothing changes until you remove something.
                    </p>
                )}
            </div>

            <div className="uapi-modules uapi-modules-standalone">
                <div className="uapi-section-bar">
                    <h2 className="uapi-section-title">Credential</h2>
                </div>

                {/* THE shared selector — chip, add button and full-screen picker in one, the same control
                    the wizard used to choose this credential in the first place. */}
                <CredentialSelector
                    marketplaceId={access?.marketplace_id}
                    hotelId={Number(hotelId)}
                    accountId={access?.account_id || selectedAccount}
                    partnerId={selectedPartner || solePartnerId}
                    value={credential}
                    // The credential and its per-hotel values in one pick, asked in the picker with Save (the shared
                    // pattern). HOTELID has a column of its own on the binding — it is the property code that system
                    // knows this hotel by, and every screen reads it from there; the rest are credential_overrides.
                    overridesInPicker
                    overrides={joinOverrides(hotelidOverride, overrides)}
                    onChange={(sel, values) => {
                        const split = splitOverrides(values || {});
                        setCredential(sel);
                        setOverrides(sel ? (split.credentialOverrides || {}) : {});
                        setHotelidOverride(sel ? (split.hotelidOverride || '') : '');
                    }}
                    canEdit={isSuperAdmin}
                    title={null}
                    subtitle={`The credential the Universal API uses to call ${access?.system_name} for this hotel.`}
                    showTest
                />
            </div>

            <div className="uapi-edit-footer">
                <button
                    type="button"
                    className="pp-criteria-add-btn pp-criteria-add-btn-danger"
                    onClick={() => setConfirmDelete(true)}
                    disabled={saving}
                >
                    <Trash2 size={12} /> Remove backend system
                </button>
                <div className="uapi-edit-footer-right">
                    <button
                        type="button"
                        className="pp-criteria-add-btn"
                        onClick={() => navigate(hotelUrl)}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="pp-rules-add-btn"
                        onClick={save}
                        disabled={saving || !credential?.credential_id}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>

            {confirmDelete && (
                <ConfirmModal
                    title="Remove backend system"
                    message={`Stop routing ${access?.hotelname} to ${access?.system_name}? `
                        + `The identifier ${access?.uapi_hotel_id} is retired with it, so anything still `
                        + 'sending it stops being routed. Adding the system again issues a new one.'}
                    confirmLabel="Remove"
                    danger
                    onConfirm={() => { setConfirmDelete(false); remove(); }}
                    onCancel={() => setConfirmDelete(false)}
                />
            )}
        </div>
    );
}

export default UapiAccessEditPage;
